import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Box from '@mui/material/Box';

import { AppProvider, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import { useAuth } from 'contexts/AuthContext';
import { NoticeCountProvider } from 'contexts/NoticeCountContext';

import AppTitle from 'components/layout/AppTitle';
import ToolbarAccount from 'components/layout/ToolbarAccount';
import {
  NoticeToolbarBadge,
  dashboardTheme,
  handleAddAccount,
  handleSwitchAccount,
  signInOut,
  toSession,
} from 'layouts/router/common';
import ScreenGuard from 'routes/ScreenGuard';

/**
 * 経費ドメインのレイアウト。
 * サイドバーナビゲーションは表示しない（Home のダッシュボード + 画面内導線で遷移する）。
 * 経費は個人の家計簿であり、Home（簡易）/ Dashboard（詳細）/ 経費一覧 / ラベル管理 / 店舗管理で構成する。
 */
const ExpenseLayout: React.FC = () => {
  const { userSession } = useAuth();

  const router = useRouter('/expense');

  const authentication = React.useMemo(() => signInOut, []);

  const ToolbarAccountSlot = React.useCallback(
    () => (
      <ToolbarAccount
        userSession={userSession}
        onSignOut={signInOut.signOut}
        onSwitchAccount={handleSwitchAccount}
        onAddAccount={handleAddAccount}
      />
    ),
    [userSession],
  );

  const ToolbarActionsSlot = React.useCallback(
    () => (
      <NoticeToolbarBadge
        allowedScreens={userSession.allowedScreens}
        userName={userSession.userName}
      />
    ),
    [userSession.allowedScreens, userSession.userName],
  );

  return (
    <NoticeCountProvider>
      <AppProvider
        session={toSession(userSession)}
        authentication={authentication}
        navigation={[]}
        router={router}
        theme={dashboardTheme}
      >
        <DashboardLayout
          hideNavigation
          slots={{
            appTitle: ExpenseAppTitle,
            toolbarActions: ToolbarActionsSlot,
            toolbarAccount: ToolbarAccountSlot,
          }}
        >
          <Box
            sx={{
              pt: 1.5,
              pl: 2,
              pr: 2,
              pb: 1.5,
              height: 'calc(100vh - 64px)',
              overflow: 'hidden',
            }}
          >
            <ScreenGuard allowedScreens={userSession.allowedScreens}>
              <Outlet />
            </ScreenGuard>
          </Box>
        </DashboardLayout>
      </AppProvider>
    </NoticeCountProvider>
  );
};

function useRouter(initialPath: string): Router {
  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => {
        navigate(String(path));
        setPathname(String(path));
      },
    };
  }, [pathname, navigate]);

  return router;
}

function ExpenseAppTitle() {
  return <AppTitle icon={<ReceiptLongIcon />} title="経費管理" color="#ef6c00" />;
}

export default ExpenseLayout;
